import { HeadingLevel, Paragraph, Table, TextRun } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  CONTENT_WIDTH_LANDSCAPE,
  h1,
  h2,
  table,
} from "@/lib/documentGenerator/docTheme";
import {
  HANDOVER_DOC_TITLES,
  HANDOVER_SECTIONS,
  HandoverKind,
  QA_COLUMNS,
  QA_DOC_TITLE,
  QA_INTRO_SECTIONS,
} from "@/lib/basProcess/constants";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

const bulletList = (items: string[]) =>
  items.map((b) => new Paragraph({ text: b, bullet: { level: 0 }, spacing: { after: 60 } }));

const signatureTable = (parties: string[], value: any) =>
  table(
    ["Part", "Namn", "Datum", "Signatur"],
    parties.map((p) => [p, value?.[p]?.name, value?.[p]?.date, value?.[p]?.sign]),
    { columnWidths: [2400, 2600, 1600, 2760], zebra: false }
  );

export const generateHandoverWordDocument = async (
  name: string,
  kind: HandoverKind,
  content: Record<string, Record<string, any>>,
  projectName?: string
) => {
  const sections = HANDOVER_SECTIONS[kind] || HANDOVER_SECTIONS.basu;
  const documentName = HANDOVER_DOC_TITLES[kind];
  const head = content["huvud"] || {};

  const docNumberByKind: Record<HandoverKind, string> = {
    basu: "20",
    byggherre: "22",
    slutsammanfattning: "34",
    utseende_basp: "05",
    utseende_basu: "05",
  };
  const docNumber = docNumberByKind[kind] ?? null;

  const stamp = await createStampForCurrentUser({
    docNumber,
    documentName,
    projectName: head.projektnamn || projectName,
  });

  const children: (Paragraph | Table)[] = [];

  // Dokumenthuvudet skrivs inte ut två gånger – uppgifterna finns i försättstabellen
  const printable = sections.filter(
    (s) => !((kind === "byggherre" || kind === "slutsammanfattning") && s.key === "huvud")
  );

  printable.forEach((s, i) => {
    children.push(h1(`${i + 1}. ${s.title}`));
    (s.intro || []).forEach((p) => children.push(body(p)));
    if (s.bullets) children.push(...bulletList(s.bullets));
    (s.outro || []).forEach((p) => children.push(body(p)));

    const value = content[s.key] || {};
    const plainFields = s.fields.filter((f) => f.type !== "checklist" && f.type !== "signature");

    if (plainFields.length) {
      children.push(
        table(
          ["Punkt", "Innehåll"],
          plainFields.map((f) => [f.label, value[f.key]]),
          { columnWidths: [3360, 6000] }
        )
      );
    }

    s.fields
      .filter((f) => f.type === "checklist")
      .forEach((f) => {
        children.push(h2(f.label));
        (f.items || []).forEach((item) =>
          children.push(
            new Paragraph({
              spacing: { after: 40 },
              children: [new TextRun({ text: `${value[f.key]?.[item] ? "☒" : "☐"} ${item}` })],
            })
          )
        );
      });

    s.fields
      .filter((f) => f.type === "signature")
      .forEach((f) => {
        children.push(h2(f.label));
        children.push(signatureTable(f.parties || [], value[f.key]));
      });
  });
  const footer = await documentFooter({ docNumber, documentType: `overlamning_${kind}` });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber,
      documentName,
      projectName: head.projektnamn || projectName,
      version: head.version || "1",
      status: head.status,
      extra: head.projektnummer ? [["Projektnummer", head.projektnummer]] : undefined,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  const filenameByKind: Partial<Record<HandoverKind, string>> = {
    slutsammanfattning: "34_Overlamningssammanfattning.docx",
    utseende_basp: "05_Uppdragsbekraftelse_Bas-P.docx",
    utseende_basu: "05_Uppdragsbekraftelse_Bas-U.docx",
  };
  const filename =
    filenameByKind[kind] || `${HANDOVER_DOC_TITLES[kind].slice(0, 40).replace(/\s+/g, "_")}.docx`;

  await downloadWordDocument(doc, filename, {
    documentType: `overlamning_${kind}`,
    docNumber: docNumber ?? undefined,
    documentName: name || documentName,
    snapshot: content,
    stamp,
  });
};

export const generateQaLogWordDocument = async (
  entries: Array<Record<string, any>>,
  projectName?: string,
  meta?: { projectNumber?: string; version?: string; status?: string }
) => {
  const stamp = await createStampForCurrentUser({
    docNumber: "25",
    documentName: QA_DOC_TITLE,
    projectName,
  });

  const children: (Paragraph | Table)[] = [];

  QA_INTRO_SECTIONS.forEach((s, i) => {
    children.push(h1(`${i + 1}. ${s.title}`));
    s.paragraphs.forEach((p) => children.push(body(p)));
    if (s.bullets) children.push(...bulletList(s.bullets));
  });

  children.push(h2("Så läses tabellen"));
  children.push(
    table(
      ["Kolumn", "Förklaring"],
      QA_COLUMNS.map((c) => [c.label, c.hint]),
      { columnWidths: [3600, 10000], totalWidth: CONTENT_WIDTH_LANDSCAPE }
    )
  );

  children.push(h1(`${QA_INTRO_SECTIONS.length + 1}. Lista hanterade ärenden`));

  // Tabellen har exakt mallens kolumner
  const tableColumns = QA_COLUMNS.filter((c) => c.key !== "decision_ref");
  const rows = entries.map((e) =>
    tableColumns.map((c) =>
      c.key === "subject"
        ? [e.subject || "", e.decision_ref ? `Ärende-ID: ${e.decision_ref}` : ""]
            .filter(Boolean)
            .join("\n")
        : e[c.key] || ""
    )
  );

  children.push(
    table(
      tableColumns.map((c) => c.label),
      rows,
      { columnWidths: [2600, 2600, 2600, 1700, 1700, 2600], totalWidth: CONTENT_WIDTH_LANDSCAPE }
    )
  );
  const footer = await documentFooter({ docNumber: "25", documentType: "qa_logg" });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "25",
      documentName: QA_DOC_TITLE,
      projectName,
      version: meta?.version || "1",
      status: meta?.status,
      extra: meta?.projectNumber ? [["Projektnummer", meta.projectNumber]] : undefined,
    },
    stamp,
    landscape: true,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, "25_Andringar_och_avsteg.docx", {
    documentType: "qa_logg",
    docNumber: "25",
    documentName: QA_DOC_TITLE,
    snapshot: { entries, meta },
    stamp,
  });
};
